import * as THREE from 'three';
import renderScales from '@shared/render-scales';
import type { BoardNode } from '../../../shared/board/types';
import { isFloatingBoardNode } from '../../../shared/board/snap-rules';

export interface SurfaceLookup {
  readonly islandData: ReadonlyMap<string, { surfaceY: number }>;
  readonly bridgeTransforms?: ReadonlyMap<string, THREE.Matrix4>;
}

export const TILE_PAD_HEIGHT = renderScales.tile.padHeight;
export const TILE_PAD_TOP_OFFSET = renderScales.tile.padTopOffset;
const IDENTITY_QUATERNION = new THREE.Quaternion();
const WORLD_UP = new THREE.Vector3(0, 1, 0);

export interface TileSurfaceFrame {
  readonly position: THREE.Vector3;
  readonly quaternion: THREE.Quaternion;
  readonly up: THREE.Vector3;
}

export function islandIdForNode(nodeId: string): string | null {
  return /^(island-\d+)-/.exec(nodeId)?.[1] ?? null;
}

export function getWorldPositionForNode(
  node: BoardNode,
  surfaceLookup: SurfaceLookup,
): THREE.Vector3 {
  return getTileSurfaceFrameForNode(node, surfaceLookup).position;
}

export function getTileSurfaceFrameForNode(
  node: BoardNode,
  surfaceLookup: SurfaceLookup,
): TileSurfaceFrame {
  if (isFloatingBoardNode(node)) {
    return {
      position: new THREE.Vector3(node.position.x, node.position.y, node.position.z),
      quaternion: IDENTITY_QUATERNION.clone(),
      up: WORLD_UP.clone(),
    };
  }

  if (node.hostBridgeId != null) {
    const bridgeTransform = surfaceLookup.bridgeTransforms?.get(node.hostBridgeId);
    if (bridgeTransform != null) {
      const position = new THREE.Vector3(node.position.x, node.position.y, node.position.z);
      const quaternion = new THREE.Quaternion();
      bridgeTransform.decompose(new THREE.Vector3(), quaternion, new THREE.Vector3());
      return {
        position: position.applyMatrix4(bridgeTransform),
        quaternion,
        up: WORLD_UP.clone().applyQuaternion(quaternion).normalize(),
      };
    }
  }
  return {
    position: new THREE.Vector3(
      node.position.x,
      getSurfaceYForNode(node, surfaceLookup),
      node.position.z,
    ),
    quaternion: IDENTITY_QUATERNION.clone(),
    up: WORLD_UP.clone(),
  };
}

export function getSurfaceYForNode(node: BoardNode, surfaceLookup: SurfaceLookup): number {
  if (isFloatingBoardNode(node)) {
    return node.position.y;
  }

  if (node.hostBridgeId != null) {
    const bridgeTransform = surfaceLookup.bridgeTransforms?.get(node.hostBridgeId);
    if (bridgeTransform != null) {
      return new THREE.Vector3(node.position.x, node.position.y, node.position.z).applyMatrix4(
        bridgeTransform,
      ).y;
    }
  }

  const islandId = islandIdForNode(node.id);
  return islandId != null
    ? (surfaceLookup.islandData.get(islandId)?.surfaceY ?? node.position.y)
    : node.position.y;
}

export function getTilePadTopYForNode(node: BoardNode, surfaceLookup: SurfaceLookup): number {
  const frame = getTileSurfaceFrameForNode(node, surfaceLookup);
  return frame.position.clone().addScaledVector(frame.up, TILE_PAD_TOP_OFFSET).y;
}
